import { deserialise, serialise } from 'kitsu-core'
import { cloneDeep } from 'lodash'
import { getRequest, patchRequest, postRequest } from '@/plugins/api-axios'
import { getURL, projectBaseUrl, getRequestHeaders } from '@/utils/axios'

import { openDialog } from '@/_components/dialog/dialog'
import { initDialog } from '@/utils/dialog'

import ProjectWizzardSettings from '@/views/Project/Settings/views/Wizzard.vue'
import ProjectClose from '@/views/Project/Project/dialogs/projectClose.vue'
import PostProjectModule from '@/views/Project/Settings/dialogs/postProjectModule.vue'
import PostModuleBklr from '@/views/Project/Settings/dialogs/postModuleBklr.vue'
import PatchProjectModule from '@/views/Project/Settings/dialogs/patchProjectModule.vue'
import PostProjectGroupDialog from '@/views/User/views/MyProjects/dialogs/postProjectGroup.vue'

export const projectSettingsFields =
    'id,customId,bklrId,name,timeZone,description,avatarFileId,address,currency,emergencyNumber,agreementUrl,itbaseUrl,customerReference,orderReference,invoiceRecipient,hseBoardDescription,publicHseBoardDescription,isDeleted,deletedAt,permissions,hasHseContacts,canCreateBklrProject,createdAt,updatedAt,constraints,owner,modules'

export function getAbbreviation(name: string | undefined) {
    if (!name) return ''

    let abbreviation = ''
    name.split(' ').map((item: string) => {
        abbreviation = abbreviation + item.charAt(0).toUpperCase()
    })
    return abbreviation
}

export const projectsInclude = ['userSettings', 'owner']

export async function searchProjects(filters: Record<string, any>, include = projectsInclude) {
    let { data } = await getRequest(
        getURL({
            path: `projects`,
            base: projectBaseUrl,
            params: {
                ...filters,
                include: include.join(','),
            },
        }),
        getRequestHeaders('GET')
    )

    return data ? deserialise(data).data : data
}

export async function initProject(
    company,
    projectId: null | string = null,
    initialProjectData = {}
) {
    return await initDialog(ProjectWizzardSettings, { company, projectId, initialProjectData })
}

export async function initProjectClose(project: Record<string, any>) {
    return await initDialog(ProjectClose, { project })
}

export async function getProject(projectId: string, include = '', fields = null as string | null) {
    let { data } = await getRequest(
        getURL({
            path: `projects/${projectId}`,
            base: projectBaseUrl,
            params: {
                include,
                ...(fields && { 'fields[project]': fields }),
            },
        }),
        getRequestHeaders('GET')
    )

    return data ? deserialise(data).data : data
}

export async function postProject(project, include = '', fields = null as string | null) {
    let payload = serialise('project', project)

    let { data } = await postRequest(
        getURL({
            path: `projects`,
            base: projectBaseUrl,
            params: {
                include,
                ...(fields && { 'fields[project]': fields }),
            },
        }),
        getRequestHeaders('POST'),
        payload.data
    )

    return data ? deserialise(data).data : data
}

export async function createBklrProject(projectId: string, updatedAt: string) {
    let { data } = await postRequest(
        getURL({
            path: `projects/${projectId}/createBklrProject`,
            base: projectBaseUrl,
        }),
        getRequestHeaders('POST', updatedAt)
    )

    return data ? deserialise(data).data : data
}

export async function patchProject(project, include = '', fields = null as string | null) {
    console.log('patchProject', fields)
    let payload = serialise('project', project)

    let { data } = await patchRequest(
        getURL({
            path: `projects/${project.id}`,
            base: projectBaseUrl,
            params: {
                include,
                ...(fields && { 'fields[project]': fields }),
            },
        }),
        getRequestHeaders('PATCH'),
        payload.data
    )

    return data ? deserialise(data).data : data
}

export async function patchProjectUserSettings(userSettings) {
    let payload = serialise(userSettings.type, cloneDeep(userSettings))

    let { data } = await patchRequest(
        getURL({
            path: `projectUserSettings/${userSettings.id}`,
            base: projectBaseUrl,
        }),
        getRequestHeaders('PATCH'),
        payload.data
    )

    return data ? deserialise(data).data : data
}

export async function toggleIsFavoriteOnProject(userSettings) {
    const payload = {
        id: userSettings.id,
        type: userSettings.type,
        isFavorite: !userSettings.isFavorite,
    }

    return await patchProjectUserSettings(payload)
}

export async function toggleIsHiddenOnProject(userSettings) {
    const payload = {
        id: userSettings.id,
        type: userSettings.type,
        isHidden: !userSettings.isHidden,
    }

    return await patchProjectUserSettings(payload)
}

// Project Group
export const projectGroupsInclude = ['userSettings', 'projects']

export async function searchProjectGroups(include = projectGroupsInclude) {
    let { data } = await getRequest(
        getURL({
            path: `projectGroups`,
            base: projectBaseUrl,
            params: {
                include: include.join(','),
            },
        }),
        getRequestHeaders('GET')
    )

    return data ? deserialise(data).data : data
}

export async function initiatePostProjectGroup() {
    return await openDialog(PostProjectGroupDialog, {})
}

export async function postProjectGroup(projectGroup, include = projectGroupsInclude) {
    let payload = serialise('projectGroup', projectGroup)

    let { data } = await postRequest(
        getURL({
            path: `projectGroups`,
            base: projectBaseUrl,
            params: {
                include,
            },
        }),
        getRequestHeaders('POST'),
        payload.data
    )

    return data ? deserialise(data).data : data
}

export async function patchProjectGroup(projectGroup, include = projectGroupsInclude) {
    let payload = serialise('projectGroup', cloneDeep(projectGroup))

    let { data } = await patchRequest(
        getURL({
            path: `projectGroups/${projectGroup.id}`,
            base: projectBaseUrl,
            params: {
                include,
            },
        }),
        getRequestHeaders('PATCH'),
        payload.data
    )

    return data ? deserialise(data).data : data
}

export async function patchProjectGroupUserSettings(userSettings) {
    let payload = serialise(userSettings.type, cloneDeep(userSettings))

    let { data } = await patchRequest(
        getURL({
            path: `projectGroupUserSettings/${userSettings.id}`,
            base: projectBaseUrl,
        }),
        getRequestHeaders('PATCH'),
        payload.data
    )

    return data ? deserialise(data).data : data
}

export async function toggleIsFavoriteOnProjectGroup(userSettings) {
    const payload = {
        id: userSettings.id,
        type: userSettings.type,
        isFavorite: !userSettings.isFavorite,
    }

    return await patchProjectGroupUserSettings(payload)
}

export async function toggleIsHiddenOnProjectGroup(userSettings) {
    const payload = {
        id: userSettings.id,
        type: userSettings.type,
        isHidden: !userSettings.isHidden,
    }

    return await patchProjectGroupUserSettings(payload)
}

// Module
export const moduleStatuses = ['active', 'archived', 'closed', 'deleted']
export async function initPostModule(moduleId: string, project) {
    return await initDialog(PostProjectModule, { moduleId, project })
}

export async function initBklrInfo() {
    return await initDialog(PostModuleBklr, {})
}

export async function postProjectModule(moduleId, project, params) {
    let payload = serialise('projectModule', {
        type: 'projectModule',
        project: {
            data: {
                type: 'project',
                id: project.id,
            },
        },
        module: {
            data: {
                type: 'module',
                id: moduleId,
            },
        },
    })

    let { data } = await postRequest(
        getURL({
            path: `projectModules`,
            base: projectBaseUrl,
            params,
        }),
        getRequestHeaders('POST'),
        payload.data
    )

    return data ? deserialise(data).data : data
}
export async function initPatchModule(project, projectModule, status) {
    return await initDialog(PatchProjectModule, { project, projectModule, status })
}
export async function patchProjectModule(projectModule, status: string) {
    let payload = serialise('projectModule', {
        type: 'projectModule',
        id: projectModule.id,
        status: status,
    })

    let { data } = await patchRequest(
        getURL({
            path: `projectModules/${projectModule.id}`,
            base: projectBaseUrl,
            params: {},
        }),
        getRequestHeaders('PATCH'),
        payload.data
    )

    return data ? deserialise(data).data : data
}
